import React, { useState } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { addTeamNote, updateNote, deleteNote } from '../../../services/sprintHealthService';
import { A, ABtn, Panel, Cap } from './reviewUi';
import RichNoteEditor from './RichNoteEditor';
import { sanitizeNoteHtml, htmlFromText, hasNoteText } from './noteHtml';

const listStyles = { '& ul, & ol': { m: '4px 0', pl: 2.75 }, '& p': { m: 0 } };

/** A note's text: formatted when it has a formatted version, otherwise plain. */
const NoteBody = ({ n }) => (n.html
  ? <Box sx={{ ...listStyles, textDecoration: n.hidden ? 'line-through' : 'none' }} dangerouslySetInnerHTML={{ __html: sanitizeNoteHtml(n.html) }} />
  : <Box component="p" sx={{ m: 0, textDecoration: n.hidden ? 'line-through' : 'none', whiteSpace: 'pre-wrap' }}>{n.text}</Box>);

/**
 * Notes for leadership: AI drafts and team notes. The team writes with bold, italic, bullets and numbers.
 * `fill` stretches the panel to the height of the tiles beside it.
 */
const NotesPanel = ({ notes, editable, sprint, team, onChange, onDraft, drafting, fill }) => {
  const [html, setHtml] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState(null);
  const list = notes?.notes || [];
  const call = async (fn) => {
    setError(null);
    try { const res = await fn(); onChange(res.data); return true; } catch (err) { setError(err.message); return false; }
  };
  const add = async () => {
    if (!hasNoteText(html)) return;
    if (await call(() => addTeamNote(sprint, team, '', sanitizeNoteHtml(html)))) { setHtml(''); setResetKey((k) => k + 1); }
  };
  return (
    <Panel sx={fill ? { height: '100%', display: 'flex', flexDirection: 'column' } : undefined}>
      <Box sx={{ px: 2.5, py: 2.25, display: 'flex', flexDirection: 'column', gap: 0, ...(fill && { flex: 1, minHeight: 0 }) }}>
        <Typography component="h2" sx={{ fontSize: 15, fontWeight: 600, color: A.ink }}>Notes for leadership</Typography>
        <Cap sx={{ mt: 0.25 }}>Win, risk or context worth knowing. The team can edit, hide or add notes. Use the list buttons for bullets or numbers.</Cap>
        {error && <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
        {notes?.error && !list.some((n) => n.source !== 'team') && <Alert severity="warning" sx={{ mt: 1.5 }}>Drafting AI notes failed: {notes.error}</Alert>}

        {list.length === 0 ? (
          <Box sx={{ mt: 1.5, p: 1.75, border: `1px dashed ${A.line}`, borderRadius: '10px', color: A.ink2, fontSize: 13 }}>
            No notes yet. {editable ? "Draft suggestions from this sprint's data, or write your own." : 'Notes are drafted on the last day of the sprint.'}
            {editable && <Box sx={{ mt: 1.25 }}><ABtn onClick={onDraft} disabled={drafting}>{drafting ? 'Drafting…' : 'Draft notes with AI'}</ABtn></Box>}
          </Box>
        ) : (
          <Box component="ul" sx={{ listStyle: 'none', mt: 1.5, mb: 0, p: 0, display: 'grid', gap: 1 }}>
            {list.map((n) => (
              <Box component="li" key={n.idx} sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 1, px: 1.5, py: 1.25, border: `1px solid ${A.line}`, borderRadius: '10px', fontSize: 13, color: A.ink, opacity: n.hidden ? 0.5 : 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ fontSize: 11, fontWeight: 600, color: n.source === 'team' ? A.ink3 : A.sageInk }}>{n.source === 'team' ? 'Team note' : 'AI draft'}{n.edited ? ' · edited' : ''}</Box>
                  {editing === n.idx ? (
                    <Box sx={{ mt: 0.5 }}>
                      <RichNoteEditor id={`note-edit-${n.idx}`} ariaLabel="Edit note" initialHtml={n.html ? sanitizeNoteHtml(n.html) : htmlFromText(n.text)} onChange={setDraft} minHeight={64} />
                    </Box>
                  ) : (
                    <NoteBody n={n} />
                  )}
                </Box>
                {editable && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', '& button': { border: 0, bgcolor: 'transparent', color: A.ink3, p: '0 4px', fontSize: 12, cursor: 'pointer', font: 'inherit' }, '& button:hover': { color: A.ink }, '& button:disabled': { opacity: 0.5, cursor: 'default' } }}>
                    {editing === n.idx ? (
                      <>
                        <button type="button" disabled={!hasNoteText(draft)} onClick={async () => { if (await call(() => updateNote(sprint, team, n.idx, { html: sanitizeNoteHtml(draft) }))) setEditing(null); }}>Save</button>
                        <button type="button" onClick={() => setEditing(null)}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button type="button" onClick={() => { setEditing(n.idx); setDraft(n.html ? sanitizeNoteHtml(n.html) : htmlFromText(n.text)); }}>Edit</button>
                        <button type="button" onClick={() => call(() => updateNote(sprint, team, n.idx, { hidden: !n.hidden }))}>{n.hidden ? 'Show' : 'Hide'}</button>
                        <button type="button" onClick={() => call(() => deleteNote(sprint, team, n.idx))}>Delete</button>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}

        {editable && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5, ...(fill && { flex: 1, minHeight: 0 }) }}>
            <RichNoteEditor id="rail-note-new" ariaLabel="New note" placeholder="Add a note, e.g. a win, a risk or context leadership should know" resetKey={resetKey} onChange={setHtml} minHeight={120} fill={fill} />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
              <ABtn variant="ghost" sx={{ px: 0.75 }} onClick={onDraft} disabled={drafting}>{drafting ? 'Drafting…' : list.some((n) => n.source !== 'team') ? 'Redraft with AI' : 'Draft with AI'}</ABtn>
              <ABtn variant="primary" onClick={add} disabled={!hasNoteText(html)}>Add note</ABtn>
            </Box>
          </Box>
        )}
      </Box>
    </Panel>
  );
};

export default NotesPanel;
